<template>
  <v-container fluid class="pa-0">
    <!-- Si no hay un ciclo cargado, mostrar alerta -->
    <v-row v-if="!cicloActivo || !cicloActivo.id">
      <v-col cols="12" class="text-center py-10">
        <v-icon size="64" color="grey lighten-1">mdi-alert-circle-outline</v-icon>
        <h3 class="text-h6 grey--text text--darken-2 mt-4">No hay un Ciclo Activo seleccionado</h3>
        <p class="grey--text">Para configurar la distribución, primero debes crear o seleccionar un ciclo en la pestaña de Configuración Inicial.</p>
        <v-btn color="primary" outlined @click="cargarDistribucion(cicloId)" :loading="cargandoCiclo">
          <v-icon left>mdi-refresh</v-icon> Intentar Cargar Ciclo Reciente
        </v-btn>
      </v-col>
    </v-row>

    <!-- Interfaz de Distribución (Solo visible si hay ciclo) -->
    <div v-else>
      <!-- Resumen del Ciclo Header -->
      <v-card outlined class="rounded-lg pa-4 mb-4 bg-light-blue border-primary">
        <v-row align="center">
          <v-col cols="12" md="4" class="py-1">
            <span class="text-caption grey--text text--darken-1 text-uppercase font-weight-bold">Ciclo Activo</span>
            <div class="text-subtitle-1 font-weight-bold primary--text">
              {{ cicloActivo.id_ciclo }} ({{ instancia }})
            </div>
          </v-col>
          <v-col cols="12" md="4" class="py-1">
            <span class="text-caption grey--text text--darken-1 text-uppercase font-weight-bold">Bolsa Total Asignada</span>
            <div class="text-subtitle-1 font-weight-bold green--text text--darken-2">
              {{ cicloActivo.bolsa_total | currency }}
            </div>
          </v-col>
          <v-col cols="12" md="4" class="py-1 text-md-right">
            <v-chip color="primary" label small class="mr-2">NI: {{ cicloActivo.porcentaje_ni }}%</v-chip>
            <v-chip color="orange" dark label small>RI: {{ cicloActivo.porcentaje_ri }}%</v-chip>
          </v-col>
        </v-row>
      </v-card>

      <v-row>
        <!-- Columna Izquierda: Matriz de Canales por Semana -->
        <v-col cols="12" md="7">
          <v-card outlined class="rounded-lg pa-4 fill-height">
            <div class="d-flex align-center justify-space-between mb-4">
              <div class="d-flex align-center">
                <v-icon color="secondary" class="mr-2">mdi-chart-donut-variant</v-icon>
                <h3 class="text-h6 font-weight-bold mb-0">Distribución por Canal (Semanal)</h3>
              </div>
            </div>

            <p class="text-body-2 grey--text text--darken-2 mb-4">
              Desglosa qué porcentaje de la bolsa asignada a cada semana se destinará a cada canal publicitario. La suma por semana debe ser <strong class="red--text">100%</strong>.
            </p>

            <v-expansion-panels v-model="panelAcordeon" multiple accordion flat dense>
              <v-expansion-panel v-for="(semana, sIdx) in semanas" :key="sIdx" class="mb-2 border-all rounded-lg">
                <v-expansion-panel-header class="py-2 bg-light-grey rounded-lg">
                  <div class="d-flex justify-space-between align-center w-100">
                    <div>
                      <strong class="primary--text">S{{ semana.numero_semana }}:</strong> 
                      {{ formatFecha(semana.fecha_inicio_semana) }} - {{ formatFecha(semana.fecha_fin_semana) }}
                      <span class="ml-2 text-caption grey--text">(Peso: {{ semana.peso_porcentual || 0 }}%)</span>
                    </div>
                    <div>
                      <v-chip small :color="esValidaMatrizSemanas(semana) ? 'success' : 'error'" outlined>
                        {{ calcularSumaCanales(semana) }}%
                      </v-chip>
                    </div>
                  </div>
                </v-expansion-panel-header>
                
                <v-expansion-panel-content class="pt-3">
                  <v-simple-table dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left font-weight-bold">Canal</th>
                          <th class="text-right font-weight-bold" style="width: 120px;">% Asignado</th>
                          <th class="text-right font-weight-bold" style="width: 150px;">Monto Proyectado</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(canal, cIdx) in canalesBase" :key="cIdx">
                          <td class="text-body-2 d-flex align-center">
                            <v-icon small class="mr-2" :color="getCanalColor(canal)">{{ getCanalIcon(canal) }}</v-icon>
                            {{ canal }}
                          </td>
                          <td>
                            <v-text-field
                              v-model.number="semana.distribucionCanal[canal]"
                              type="number"
                              suffix="%"
                              dense
                              outlined
                              hide-details
                              class="input-right input-small"
                              :min="0" :max="100"
                              :disabled="!puedeEditar"
                            ></v-text-field>
                          </td>
                          <td class="text-right font-weight-medium text-body-2">
                            {{ calcularCostoCanalMonto(semana, canal) | currency }}
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            
            <v-btn 
              color="primary" 
              class="mt-4" 
              block 
              elevation="1" 
              :disabled="!todasSemanasValidas || !puedeEditar"
              :loading="loadingGuardarCanales"
              @click="confirmarGuardadoCanales"
            >
              <v-icon left>mdi-content-save</v-icon> Guardar Matriz de Canales
            </v-btn>
            
          </v-card>
        </v-col>

        <!-- Columna Derecha: Pesos Diarios -->
        <v-col cols="12" md="5">
          <v-card outlined class="rounded-lg pa-4 fill-height">
            <div class="d-flex align-center justify-space-between mb-4">
              <div class="d-flex align-center">
                <v-icon color="secondary" class="mr-2">mdi-chart-timeline-variant-shimmer</v-icon>
                <h3 class="text-h6 font-weight-bold mb-0">Pesos Diarios</h3>
              </div>
            </div>

            <p class="text-body-2 grey--text text--darken-2 mb-4">
              Asigna el peso proporcional del gasto para cada día de la semana. Fines de semana suelen llevar mayor carga. La suma total debe ser <strong class="red--text">100%</strong>.
            </p>

            <v-expansion-panels v-model="panelAcordeonDias" multiple accordion flat dense>
              <v-expansion-panel v-for="(semana, sIdx) in semanas" :key="sIdx" class="mb-2 border-all rounded-lg">
                <v-expansion-panel-header class="py-2 bg-light-grey rounded-lg">
                  <div class="d-flex justify-space-between align-center w-100">
                    <div>
                      <strong class="primary--text">S{{ semana.numero_semana }}</strong>
                      <span class="ml-2 text-caption grey--text d-none d-sm-inline">{{ formatFecha(semana.fecha_inicio_semana) }} - {{ formatFecha(semana.fecha_fin_semana) }}</span>
                    </div>
                    <div>
                      <v-chip dark small :color="esValidaMatrizDiasSemana(semana) ? 'success' : 'error'">
                        {{ calcularSumaDiasSemana(semana) }}%
                      </v-chip>
                    </div>
                  </div>
                </v-expansion-panel-header>

                <v-expansion-panel-content class="pt-3 pb-0">
                  <v-list dense class="transparent pa-0">
                    <v-list-item v-for="(dia, dIdx) in semana.diasSemana" :key="dIdx" class="mb-1 rounded-lg border-all bg-white py-1">
                      <v-list-item-content style="width: 80%;">
                        <v-list-item-title class="font-weight-medium text-body-2 text-uppercase d-flex justify-space-between align-center">
                          <span>{{ dia.nombre }}</span>
                          <span class="primary--text font-weight-bold">{{ calcularMontoDia(semana, dia.peso) | currency }}</span>
                        </v-list-item-title>
                        <v-row class="d-flex align-center" style="width: 130px;">
                          <v-col cols="8">
                            <v-slider
                              v-model="dia.peso"
                              min="0"
                              max="50"
                              hide-details
                            class="mr-2"
                            color="primary"
                            :disabled="!puedeEditar"
                          ></v-slider>
                          </v-col>
                          <v-col cols="4" class="d-flex align-center justify-center">
                          <v-text-field
                            v-model.number="dia.peso"
                            suffix="%"
                            type="number"
                            dense
                            outlined
                            hide-details
                            class="input-small flex-shrink-1"
                            style="max-width: 100px;"
                            :disabled="!puedeEditar"
                          ></v-text-field>
                          </v-col>
                        </v-row>

                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-alert
              :type="todasSemanasDiasValidas ? 'success' : 'error'"
              dense
              outlined
              class="mt-4 mb-2 d-flex justify-center flex-column text-center"
            >
              <div class="font-weight-bold" v-if="todasSemanasDiasValidas">
                Todas las semanas cuadran al 100%
              </div>
              <div class="text-caption font-weight-bold" v-else>
                Cada semana debe sumar exactamente 100%
              </div>
            </v-alert>

            <v-btn 
              color="primary" 
              class="mt-4" 
              block 
              elevation="1" 
              :disabled="!todasSemanasDiasValidas || !puedeEditar"
              :loading="loadingGuardarDias"
              @click="confirmarGuardadoDias"
            >
              <v-icon left>mdi-calendar-sync</v-icon> Guardar Pesos Diarios
            </v-btn>
            
          </v-card>
        </v-col>
      </v-row>
    <!-- Modal Confirmar Guardado Canales -->
    <v-dialog v-model="dialogConfirmarCanales" max-width="400" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="text-h6 pb-2">
          <v-icon left color="warning">mdi-content-save-alert</v-icon>
          Guardar Matriz de Canales
        </v-card-title>
        <v-card-text class="pt-2 text-body-1">
          ¿Estás seguro de que deseas sobreescribir la distribución presupuestal por canales? <br><br>
          Esta acción recalculará los montos proyectados para cada plataforma publicitaria.
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer/>
          <v-btn text @click="dialogConfirmarCanales = false" :disabled="loadingGuardarCanales">Cancelar</v-btn>
          <v-btn color="primary" elevation="1" :loading="loadingGuardarCanales" @click="procesarGuardarCanales">
            <v-icon left small>mdi-check</v-icon> Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Modal Confirmar Guardado Días -->
    <v-dialog v-model="dialogConfirmarDias" max-width="400" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="text-h6 pb-2">
          <v-icon left color="warning">mdi-calendar-sync</v-icon>
          Guardar Pesos Diarios
        </v-card-title>
        <v-card-text class="pt-2 text-body-1">
          ¿Estás seguro de que deseas actualizar la ponderación de gasto diario? <br><br>
          Esto definirá qué porcentaje de la bolsa semanal se consumirá cada día de la semana.
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer/>
          <v-btn text @click="dialogConfirmarDias = false" :disabled="loadingGuardarDias">Cancelar</v-btn>
          <v-btn color="primary" elevation="1" :loading="loadingGuardarDias" @click="procesarGuardarDias">
            <v-icon left small>mdi-check</v-icon> Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </div>
  </v-container>
</template>

<script>
import moment from 'moment';

export default {
  name: 'TabDistribucion',
  props: {
    instancia: { type: String, required: true },
    isDirector: { type: Boolean, default: false },
    cicloId: { type: [Number, String], default: null },
    isActivo: { type: Boolean, default: false }
  },
  
  data() {
    return {
      cargandoCiclo: false,
      cicloActivo: null,
      semanas: [],
      panelAcordeon: [0], // Abrir solo la primera semana por defecto
      
      loadingGuardarCanales: false,
      loadingGuardarDias: false,
      dialogConfirmarCanales: false,
      dialogConfirmarDias: false,

      panelAcordeonDias: [0],
      
      loadingGuardarCanales: false,
      loadingGuardarDias: false,
      dialogConfirmarCanales: false,
      dialogConfirmarDias: false,

      canalesBase: ['Meta Ads', 'Google Ads', 'TikTok Ads', 'Influencers', 'Otros']
    };
  },

  computed: {
    puedeEditar() {
      return this.isDirector && this.isActivo;
    },
    todasSemanasDiasValidas() {
      if(this.semanas.length === 0) return false;
      return this.semanas.every(sem => this.esValidaMatrizDiasSemana(sem));
    },

    todasSemanasValidas() {
      if(this.semanas.length === 0) return false;
      return this.semanas.every(sem => this.esValidaMatrizSemanas(sem));
    }
  },

  watch: {
    cicloId: {
      immediate: true,
      handler(newVal) {
        this.cargarDistribucion(newVal);
      }
    }
  },

  mounted() {
    // Escuchar si desde la pestaña de Configuración avisan que crearon un ciclo nuevo
    this.$parent.$on('nuevo-ciclo-guardado', () => {
      this.cargarDistribucion(this.cicloId);
    });
  },

  methods: {
    async cargarDistribucion(id) {
      if (!id) {
        this.cicloActivo = null;
        this.semanas = [];
        return;
      }
      this.cargandoCiclo = true;
      try {
        const resCiclo = await this.$http.get(`api/presupuestos/ciclo/${id}`);
        if (resCiclo.data) {
          this.cicloActivo = resCiclo.data;
          await this.cargarSemanasDelCiclo();
          await this.cargarDiasDelCiclo();
        } else {
          this.cicloActivo = null;
        }
      } catch (error) {
        console.error("Error cargando ciclo", error);
      } finally {
        this.cargandoCiclo = false;
      }
    },

    async cargarSemanasDelCiclo() {
      if(!this.cicloActivo) return;
      
      try {
        const resSemanas = await this.$http.get(`api/presupuestos/semanas/${this.cicloActivo.id}`);
        
        if(resSemanas.data && resSemanas.data.length > 0) {
          // Parsear semanas y adjuntarles un diccionario reactivo para la UI de Canales
          this.semanas = resSemanas.data.map(sem => {
            // Inicializamos distribución en 0 para cada canal
            let distribucionObj = {};
            this.canalesBase.forEach(c => distribucionObj[c] = 0);
            
            // Asignamos pesos por defecto (Ej: 80 Meta, 20 Google) solo si es nuevo
            distribucionObj['Meta Ads'] = 70;
            distribucionObj['Google Ads'] = 30;

            const diasObj = [
              { nombre: 'Lunes',     peso: 10 },
              { nombre: 'Martes',    peso: 10 },
              { nombre: 'Miércoles', peso: 15 },
              { nombre: 'Jueves',    peso: 15 },
              { nombre: 'Viernes',   peso: 15 },
              { nombre: 'Sábado',    peso: 20 },
              { nombre: 'Domingo',   peso: 15 }
            ];

            return {
              ...sem,
              distribucionCanal: distribucionObj,
              diasSemana: diasObj
            };
          });

          // Aquí deberíamos cruzarlo con datos de base de datos si ya existen distribuciones de canales guardadas
          // Para no hacer N requests, si tuvieramos un JOIN sería ideal, pero por ahora lo dejamos como defaults en frontend
          // Se podría hacer un GET distribucion canal si el backend lo proveyera o lo agregamos a las semanas en el backend.
        }
      } catch (error) {
        console.error("Error cargando semanas", error);
      }
    },

    async cargarDiasDelCiclo() {
      if(!this.cicloActivo) return;
      try {
        const resDias = await this.$http.get(`api/presupuestos/diaria/${this.cicloActivo.id}`);
        
        if(resDias.data && resDias.data.length > 0) {
          // Mapear los pesos guardados en la BD a nuestra estructura reactiva
          resDias.data.forEach(dBD => {
            let semanaFront = this.semanas.find(s => s.id === dBD.id_semana);
            // Fallback backward compatibility si no existe id_semana (datos viejos) aplícalo a todas
            if(!dBD.id_semana) {
               this.semanas.forEach(sem => {
                  let diaFront = sem.diasSemana.find(d => d.nombre === dBD.dia_semana);
                  if(diaFront) diaFront.peso = parseFloat(dBD.peso_porcentual);
               });
            } else if (semanaFront) {
               let diaFront = semanaFront.diasSemana.find(d => d.nombre === dBD.dia_semana);
               if(diaFront) diaFront.peso = parseFloat(dBD.peso_porcentual);
            }
          });
        }
      } catch (error) {
        console.error("Error cargando pesos diarios", error);
      }
    },

    calcularSumaCanales(semana) {
      if (!semana || !semana.distribucionCanal) return 0;
      let suma = 0;
      Object.values(semana.distribucionCanal).forEach(val => suma += (parseFloat(val) || 0));
      return Math.round(suma * 100) / 100;
    },

    esValidaMatrizSemanas(semana) {
      return this.calcularSumaCanales(semana) === 100;
    },

    calcularSumaDiasSemana(semana) {
      if (!semana || !semana.diasSemana) return 0;
      const suma = semana.diasSemana.reduce((acc, curr) => acc + (parseFloat(curr.peso) || 0), 0);
      return Math.round(suma * 100) / 100;
    },

    esValidaMatrizDiasSemana(semana) {
      return this.calcularSumaDiasSemana(semana) === 100;
    },

    calcularCostoCanalMonto(semana, canal) {
      if(!this.cicloActivo || !semana.peso_porcentual) return 0;
      
      const bolsaSemana = this.cicloActivo.bolsa_total * ((semana.peso_porcentual || 0) / 100);
      const pesoCanal = (semana.distribucionCanal[canal] || 0) / 100;
      
      return bolsaSemana * pesoCanal;
    },

    calcularMontoDia(semana, peso) {
      if(!this.cicloActivo || !semana.peso_porcentual) return 0;
      const bolsaSemana = this.cicloActivo.bolsa_total * ((semana.peso_porcentual || 0) / 100);
      return bolsaSemana * (peso / 100);
    },

    formatFecha(fechaStr) {
      if(!fechaStr) return '';
      return moment(fechaStr).format('DD MMM');
    },

    getCanalColor(canal) {
      const colors = {
        'Meta Ads': '#1877F2',
        'Google Ads': '#EA4335',
        'TikTok Ads': '#000000',
        'Influencers': '#C13584',
        'Otros': 'grey'
      };
      return colors[canal] || 'primary';
    },

    getCanalIcon(canal) {
      const icons = {
        'Meta Ads': 'mdi-facebook',
        'Google Ads': 'mdi-google',
        'TikTok Ads': 'mdi-music-note',
        'Influencers': 'mdi-account-star',
        'Otros': 'mdi-shape'
      };
      return icons[canal] || 'mdi-circle';
    },

    confirmarGuardadoCanales() {
      if (!this.todasSemanasValidas) return;
      this.dialogConfirmarCanales = true;
    },

    async procesarGuardarCanales() {
      this.dialogConfirmarCanales = false;
      this.loadingGuardarCanales = true;

      try {
        const payload = {
          id_presupuesto_ciclo: this.cicloActivo.id,
          semanas: this.semanas.map(s => {
            let canalesArray = [];
            for (const [key, value] of Object.entries(s.distribucionCanal)) {
              if (parseFloat(value) > 0) {
                canalesArray.push({
                  nombre: key,
                  porcentaje_del_canal: parseFloat(value)
                });
              }
            }

            return {
              numero_semana: s.numero_semana,
              fecha_inicio_semana: moment(s.fecha_inicio_semana).format('YYYY-MM-DD'),
              fecha_fin_semana: moment(s.fecha_fin_semana).format('YYYY-MM-DD'),
              peso_porcentual: parseFloat(s.peso_porcentual) || 0,
              porcentaje_asignado_ni: this.cicloActivo.porcentaje_ni || 0,
              porcentaje_asignado_ri: this.cicloActivo.porcentaje_ri || 0,
              canales: canalesArray
            };
          })
        };

        await this.$http.post('api/presupuestos/semanas', payload);
        this.$root.$emit('mostrarAlerta', { tipo: 'success', mensaje: 'Matriz de Canales guardada' });

      } catch (error) {
        console.error("Error guardando canales", error);
        this.$root.$emit('mostrarAlerta', { tipo: 'error', mensaje: 'Error al guardar configuración de canales' });
      } finally {
        this.loadingGuardarCanales = false;
      }
    },

    confirmarGuardadoDias() {
      if (!this.todasSemanasDiasValidas) return;
      this.dialogConfirmarDias = true;
    },

    async procesarGuardarDias() {
      this.dialogConfirmarDias = false;
      this.loadingGuardarDias = true;

      try {
        const payload = {
          id_presupuesto_ciclo: this.cicloActivo.id,
          semanas_dias: this.semanas.map(s => ({
            id_semana: s.id,
            distribuciones: s.diasSemana.map(d => ({
              dia_semana: d.nombre,
              peso_porcentual: parseFloat(d.peso)
            }))
          }))
        };

        await this.$http.post('api/presupuestos/diaria', payload);
        this.$root.$emit('mostrarAlerta', { tipo: 'success', mensaje: 'Pesos diarios guardados' });

      } catch (error) {
        console.error("Error guardando pesos diarios", error);
        this.$root.$emit('mostrarAlerta', { tipo: 'error', mensaje: 'Error al guardar pesos diarios' });
      } finally {
        this.loadingGuardarDias = false;
      }
    }
  },

  filters: {
    currency(value) {
      if (typeof value !== "number") return value;
      return "$" + value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');
    }
  }
};
</script>

<style scoped>
.bg-light-blue {
  background-color: #F1F8FF;
}
.bg-light-grey {
  background-color: #fcfcfc;
}
.border-primary {
  border: 1px solid var(--v-primary-base) !important;
}
.border-all {
  border: 1px solid rgba(0,0,0,0.08);
}
.input-small :deep(input) {
  font-size: 0.85rem !important;
  text-align: center;
}
.input-right :deep(input) {
  text-align: right;
}
.transparent {
  background-color: transparent !important;
}
.w-100 {
  width: 100%;
}
</style>
